
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Search, Users, BarChart, Target, TrendingUp, MessageSquare, Mail, Star } from "lucide-react";

const DigitalMarketingPage = () => {
  useEffect(() => {
    document.title = "Digital Marketing - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Search className="w-8 h-8 text-orange-600" />,
      title: "Search Engine Optimization (SEO)",
      description: "Improve your search rankings and drive organic traffic with our proven SEO strategies"
    },
    {
      icon: <Users className="w-8 h-8 text-orange-600" />,
      title: "Social Media Marketing",
      description: "Engage your audience across Facebook, Instagram, TikTok, LinkedIn and other platforms"
    },
    {
      icon: <Target className="w-8 h-8 text-orange-600" />,
      title: "Pay-Per-Click (PPC) Advertising",
      description: "Reach your ideal customers with precision-targeted Google Ads and social media campaigns"
    },
    {
      icon: <BarChart className="w-8 h-8 text-orange-600" />,
      title: "Analytics & Reporting",
      description: "Track performance and ROI with detailed analytics, insights, and monthly reports"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Growth Marketing",
      description: "Data-driven strategies to scale your business and increase revenue exponentially"
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-orange-600" />,
      title: "Content Marketing",
      description: "Create compelling content that attracts, engages, and converts your target audience"
    },
    {
      icon: <Mail className="w-8 h-8 text-orange-600" />,
      title: "Email Marketing",
      description: "Build and nurture customer relationships with personalized email campaigns"
    },
    {
      icon: <Star className="w-8 h-8 text-orange-600" />,
      title: "Brand Management",
      description: "Build and maintain a strong online brand presence across all digital channels"
    }
  ];

  const platforms = [
    { name: "Google Ads", icon: "🔍" },
    { name: "Facebook", icon: "📘" },
    { name: "Instagram", icon: "📷" },
    { name: "TikTok", icon: "🎵" },
    { name: "LinkedIn", icon: "💼" },
    { name: "YouTube", icon: "📺" },
    { name: "Twitter", icon: "🐦" },
    { name: "Pinterest", icon: "📌" }
  ];

  const benefits = [
    "Increased Website Traffic",
    "Higher Conversion Rates",
    "Better Brand Awareness",
    "Improved Customer Engagement",
    "Cost-Effective Marketing",
    "Measurable Results",
    "Competitive Advantage",
    "Long-term Growth"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Comprehensive digital marketing services in Nepal. SEO, social media marketing, PPC advertising, content marketing, and analytics." />
        <meta name="keywords" content="digital marketing, SEO, social media marketing, PPC, Google Ads, Nepal, online advertising" />
        <meta property="og:title" content="Digital Marketing - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-orange-800 mb-4">
              Digital Marketing Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grow your business online with our strategic digital marketing solutions that deliver measurable results.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Platforms Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-orange-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Platforms We Work With</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{platform.icon}</div>
                  <h3 className="font-semibold">{platform.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Why Choose Our Digital Marketing?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Strategy Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Marketing Strategy</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Research", desc: "Market analysis and competitor research" },
                { step: "02", title: "Strategy", desc: "Custom marketing plan development" },
                { step: "03", title: "Execute", desc: "Campaign implementation and optimization" },
                { step: "04", title: "Analyze", desc: "Performance tracking and reporting" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Our Results</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-orange-600">300%</div>
                <div className="text-gray-600">Average ROI Increase</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600">150+</div>
                <div className="text-gray-600">Campaigns Managed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600">500%</div>
                <div className="text-gray-600">Traffic Growth</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600">98%</div>
                <div className="text-gray-600">Client Satisfaction</div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Boost Your Online Presence?</h2>
            <p className="text-xl text-orange-100 mb-6">
              Let's create a digital marketing strategy that drives real results for your business.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default DigitalMarketingPage;
